/**
 * Named report presets — filter snapshot only (no dates).
 * Dates are chosen on the Presets page, same as switching dates on Dashboard.
 *
 * Storage key: reportPresets_v1:<page>:<userId>
 * Item shape: { id, name, snapshot, summary, when, pinned, pinnedAt }
 */

import { TOKEN_KEY } from '../auth/authConstants';
import { userIdFromToken } from '../auth/crossTabAuth';
import { encodeReportShare } from './reportShare';
import { assertValidSavedName } from '../auth/namePolicy';
import { presetsAPI } from '../api';

const STORAGE_PREFIX = 'reportPresets_v1';
const MAX = 50;
export const PRESET_NAME_MAX = 40;
export const PRESET_PAGES = Object.freeze({
  dashboard: 'dashboard',
  reporting: 'reporting',
  roi: 'roi',
  // One entry per Ads account the admin added as a "sheet" (sidebar link under ROI); synced like presets.
  roiSheets: 'roi-sheets',
  admobRoi: 'admob-roi',
  adsenseRoi: 'adsense-roi',
  admob: 'admob',
  adsense: 'adsense',
  admobReporting: 'admob-reporting',
  adsenseReporting: 'adsense-reporting',
});
export const PRESETS_CHANGED_EVENT = 'report_presets_changed';

function currentUserId() {
  try {
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    return userIdFromToken(token) || null;
  } catch {
    return null;
  }
}

function storageKey(page, userId) {
  const id = userId || currentUserId() || 'anon';
  const p = page || PRESET_PAGES.dashboard;
  return `${STORAGE_PREFIX}:${p}:${id}`;
}

function toArray(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v.filter(Boolean) : (v ? [v] : []);
}

/** Full snapshot shape (dates optional — legacy presets may still have them). */
export function normalizePresetSnapshot(f = {}) {
  return {
    preset: f.preset || null,
    startDate: f.startDate || null,
    endDate: f.endDate || null,
    targetType: f.targetType || null,
    country: toArray(f.country),
    domain: toArray(f.domain),
    site: toArray(f.site),
    domainName: toArray(f.domainName),
    domainId: toArray(f.domainId),
    reportDimensions: toArray(f.reportDimensions),
    reportMetrics: toArray(f.reportMetrics),
    // ROI Ads filters (MultiSelect values; may include __ALL__)
    accountIds: toArray(f.accountIds),
    campaignIds: toArray(f.campaignIds),
    appKeys: toArray(f.appKeys),
    siteKeys: toArray(f.siteKeys),
    countryCodes: toArray(f.countryCodes),
    accountLabels: toArray(f.accountLabels),
    campaignLabels: toArray(f.campaignLabels),
    appLabels: toArray(f.appLabels),
    siteLabels: toArray(f.siteLabels),
    countryLabels: toArray(f.countryLabels),
    apps: toArray(f.apps),
    formats: toArray(f.formats),
    countries: toArray(f.countries),
    platforms: toArray(f.platforms),
    sites: toArray(f.sites),
  };
}

/** Strip dates from a snapshot before persisting a preset. */
export function filtersOnlySnapshot(f = {}) {
  const snap = normalizePresetSnapshot(f);
  const {
    preset,
    startDate,
    endDate,
    ...filters
  } = snap;
  return filters;
}

/** Merge saved filter preset with active date range (Presets page / open in …). */
export function mergePresetWithDates(filterSnapshot = {}, { startDate, endDate, preset } = {}) {
  return {
    ...filtersOnlySnapshot(filterSnapshot),
    ...(startDate && endDate ? { startDate, endDate } : {}),
    ...(preset ? { preset } : {}),
  };
}

function makeId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `rp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function summaryForPreset(snapshot) {
  const p = normalizePresetSnapshot(snapshot);
  const parts = [];

  if (p.targetType && p.targetType !== 'all') {
    parts.push(p.targetType === 'site' ? 'Sites' : 'Apps');
  }

  const fmt = (label, arr) => {
    const list = Array.isArray(arr) ? arr.filter(Boolean) : [];
    if (!list.length) return;
    if (list.length === 1 && list[0] === '__ALL__') {
      parts.push(`${label}: All`);
      return;
    }
    const clean = list.filter((v) => v !== '__ALL__');
    if (!clean.length) return;
    if (clean.length === 1) parts.push(`${label}: ${clean[0]}`);
    else parts.push(`${label}s (${clean.length})`);
  };

  const fmtCountOrLabels = (singular, plural, ids, labels) => {
    const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (!list.length) return;
    if (list.length === 1 && list[0] === '__ALL__') {
      parts.push(`All ${plural}`);
      return;
    }
    const clean = list.filter((v) => v !== '__ALL__');
    if (!clean.length) return;
    const names = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (clean.length === 1) {
      parts.push(`${singular}: ${names[0] || clean[0]}`);
      return;
    }
    parts.push(`${clean.length} ${plural}`);
  };

  fmt('Domain', p.domain);
  fmt('Site', p.site);
  fmt('Ad Unit', p.domainName);
  fmt('App', p.domainId);
  fmt('Country', p.country);
  fmt('App', p.apps);
  fmt('Format', p.formats);
  fmt('Country', p.countries);
  fmt('Platform', p.platforms);
  fmt('Site', p.sites);
  if (p.reportDimensions?.length) parts.push(`Dims (${p.reportDimensions.length})`);
  if (p.reportMetrics?.length) parts.push(`Metrics (${p.reportMetrics.length})`);

  fmtCountOrLabels('Account', 'accounts', p.accountIds, p.accountLabels);
  fmtCountOrLabels('Campaign', 'campaigns', p.campaignIds, p.campaignLabels);
  fmtCountOrLabels('App ID', 'apps', p.appKeys, p.appLabels);
  fmtCountOrLabels('Site', 'sites', p.siteKeys, p.siteLabels);
  fmtCountOrLabels('Country', 'countries', p.countryCodes, p.countryLabels);

  return parts.length ? parts.join(' • ') : 'Default filters';
}

function defaultNameFromSnapshot(snapshot) {
  const summary = summaryForPreset(snapshot);
  if (summary && summary !== 'Default filters') {
    return summary.slice(0, PRESET_NAME_MAX);
  }
  return 'Untitled preset';
}

function sortPresets(list) {
  return [...list].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
    const pa = a.pinnedAt || 0;
    const pb = b.pinnedAt || 0;
    if (a.pinned && b.pinned && pa !== pb) return pb - pa;
    return (b.when || 0) - (a.when || 0);
  });
}

function parseList(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const list = parsed
      .map((entry) => {
        if (!entry || !entry.snapshot) return null;
        const snap = normalizePresetSnapshot(entry.snapshot);
        const name = String(entry.name || '').trim() || defaultNameFromSnapshot(snap);
        return {
          id: entry.id || makeId(),
          name: name.slice(0, PRESET_NAME_MAX),
          snapshot: snap,
          summary: entry.summary || summaryForPreset(snap),
          when: entry.when || Date.now(),
          pinned: Boolean(entry.pinned),
          pinnedAt: entry.pinnedAt || null,
        };
      })
      .filter(Boolean);
    return sortPresets(list);
  } catch {
    return [];
  }
}

function notifyChanged() {
  try {
    window.dispatchEvent(new CustomEvent(PRESETS_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

function writeList(page, userId, list) {
  const next = sortPresets(Array.isArray(list) ? list : []).slice(0, MAX);
  localStorage.setItem(storageKey(page, userId), JSON.stringify(next));
  markDirty(page, userId);
  notifyChanged();
  scheduleServerSave(page, userId);
  return next;
}

// ─── Server sync ──────────────────────────────────────────────────────────────
// localStorage is the instant local copy. Every change is pushed to the server shortly after,
// and syncPresetsFromServer() pulls the server copy on login so presets follow the user across
// browsers. Each page's list carries a version; a push against a stale version gets the server
// copy back and the two lists are merged by preset id (newest edit wins).

const SAVE_DEBOUNCE_MS = 500;
const MAX_PUSH_ATTEMPTS = 3;
const saveTimers = new Map();

const versionKey = (page, userId) => `${storageKey(page, userId)}:v`;
const dirtyKey = (page, userId) => `${storageKey(page, userId)}:dirty`;

function readVersion(page, userId) {
  try { return parseInt(localStorage.getItem(versionKey(page, userId)), 10) || 0; } catch { return 0; }
}
function writeVersion(page, userId, v) {
  try { localStorage.setItem(versionKey(page, userId), String(v || 0)); } catch { /* ignore */ }
}
function isDirty(page, userId) {
  try { return localStorage.getItem(dirtyKey(page, userId)) === '1'; } catch { return false; }
}
function markDirty(page, userId) {
  try { localStorage.setItem(dirtyKey(page, userId), '1'); } catch { /* ignore */ }
}
function clearDirty(page, userId) {
  try { localStorage.removeItem(dirtyKey(page, userId)); } catch { /* ignore */ }
}

/** Union by id; when both sides have an id the more recently edited copy wins. */
function mergeLists(localList, serverList) {
  const byId = new Map();
  for (const item of [...serverList, ...localList]) {
    const prev = byId.get(item.id);
    if (!prev || (item.when || 0) >= (prev.when || 0)) byId.set(item.id, item);
  }
  return sortPresets([...byId.values()]).slice(0, MAX);
}

/** Store a server list locally without marking it as a pending change. */
function adoptServerList(page, userId, items, version) {
  const list = parseList(JSON.stringify(items || []));
  localStorage.setItem(storageKey(page, userId), JSON.stringify(list));
  writeVersion(page, userId, version);
  clearDirty(page, userId);
  return list;
}

async function pushPage(page, userId, attempt = 1) {
  const snapshotJson = localStorage.getItem(storageKey(page, userId)) || '[]';
  let items;
  try { items = JSON.parse(snapshotJson); } catch { items = []; }
  let res;
  try {
    res = await presetsAPI.savePage(page, items, readVersion(page, userId));
  } catch {
    return false; // offline or server error: stays dirty, retried on the next sync or reconnect
  }
  if (res && !res.error && res.version != null) {
    writeVersion(page, userId, res.version);
    // Only clear the pending flag if nothing changed while the request was in flight.
    if ((localStorage.getItem(storageKey(page, userId)) || '[]') === snapshotJson) clearDirty(page, userId);
    else scheduleServerSave(page, userId);
    return true;
  }
  if (res && res.error && Array.isArray(res.items)) {
    const merged = mergeLists(parseList(snapshotJson), parseList(JSON.stringify(res.items)));
    localStorage.setItem(storageKey(page, userId), JSON.stringify(merged));
    writeVersion(page, userId, res.version);
    markDirty(page, userId);
    notifyChanged();
    if (attempt < MAX_PUSH_ATTEMPTS) return pushPage(page, userId, attempt + 1);
  }
  return false;
}

function scheduleServerSave(page, userId) {
  const key = storageKey(page, userId);
  clearTimeout(saveTimers.get(key));
  saveTimers.set(key, setTimeout(() => {
    saveTimers.delete(key);
    pushPage(page, userId);
  }, SAVE_DEBOUNCE_MS));
}

let syncInFlight = null;

/**
 * Pull the signed-in user's presets from the server (and push any local-only or unsaved ones).
 * Safe to call often; concurrent calls share one run.
 */
export function syncPresetsFromServer(userId) {
  if (!userId) return Promise.resolve(false);
  if (syncInFlight) return syncInFlight;
  syncInFlight = (async () => {
    let remote;
    try {
      remote = (await presetsAPI.getAll())?.pages || {};
    } catch {
      return false;
    }
    let changed = false;
    for (const page of Object.values(PRESET_PAGES)) {
      const server = remote[page];
      const localList = getReportPresets(page, userId);
      if (!server) {
        // First sync from this browser: upload what is already saved locally.
        if (localList.length) { markDirty(page, userId); await pushPage(page, userId); }
        continue;
      }
      const serverList = parseList(JSON.stringify(server.items || []));
      if (isDirty(page, userId)) {
        const merged = mergeLists(localList, serverList);
        localStorage.setItem(storageKey(page, userId), JSON.stringify(merged));
        writeVersion(page, userId, server.version);
        changed = true;
        await pushPage(page, userId);
      } else if (readVersion(page, userId) !== server.version) {
        adoptServerList(page, userId, server.items, server.version);
        changed = true;
      }
    }
    if (changed) notifyChanged();
    return true;
  })().finally(() => { syncInFlight = null; });
  return syncInFlight;
}

if (typeof window !== 'undefined') {
  // Push anything that could not be saved while offline.
  window.addEventListener('online', () => {
    const uid = currentUserId();
    if (uid) syncPresetsFromServer(uid);
  });
}

export function getReportPresets(page, userId) {
  try {
    return parseList(localStorage.getItem(storageKey(page, userId)));
  } catch {
    return [];
  }
}

export function saveReportPreset(page, name, filter, userId) {
  const snap = filtersOnlySnapshot(filter);
  let trimmed;
  try {
    trimmed = assertValidSavedName(
      String(name || '').trim() || defaultNameFromSnapshot(snap),
      { maxLength: PRESET_NAME_MAX, label: 'Preset name' },
    );
  } catch (err) {
    return getReportPresets(page, userId);
  }
  try {
    const existing = getReportPresets(page, userId);
    const next = [
      {
        id: makeId(),
        name: trimmed.slice(0, PRESET_NAME_MAX),
        snapshot: snap,
        summary: summaryForPreset(snap),
        when: Date.now(),
        pinned: false,
        pinnedAt: null,
      },
      ...existing,
    ];
    return writeList(page, userId, next);
  } catch {
    return getReportPresets(page, userId);
  }
}

export function updateReportPreset(page, id, { name } = {}, userId) {
  if (!id) return getReportPresets(page, userId);
  try {
    const existing = getReportPresets(page, userId);
    const idx = existing.findIndex((item) => item.id === id);
    if (idx < 0) return existing;
    const prev = existing[idx];
    let trimmed = prev.name;
    if (name != null) {
      try {
        trimmed = assertValidSavedName(String(name).trim(), {
          maxLength: PRESET_NAME_MAX,
          label: 'Preset name',
        });
      } catch {
        return existing;
      }
    }
    if (!trimmed) return existing;
    const updated = { ...prev, name: trimmed, when: Date.now() };
    const next = existing.map((item) => (item.id === id ? updated : item));
    return writeList(page, userId, next);
  } catch {
    return getReportPresets(page, userId);
  }
}

/** Build a unique "Name (copy)" / "Name (copy 2)" within max length. */
function uniqueCopyName(baseName, existingNames) {
  const taken = new Set(
    [...(existingNames || [])].map((n) => String(n || '').trim().toLowerCase()).filter(Boolean)
  );
  const root = String(baseName || 'Untitled preset')
    .replace(/\s*\(copy(?:\s+\d+)?\)\s*$/i, '')
    .trim() || 'Untitled preset';

  const build = (suffix) => {
    const maxBase = Math.max(1, PRESET_NAME_MAX - suffix.length);
    return `${root.slice(0, maxBase)}${suffix}`.slice(0, PRESET_NAME_MAX);
  };

  let candidate = build(' (copy)');
  if (!taken.has(candidate.toLowerCase())) return candidate;
  for (let n = 2; n < 100; n += 1) {
    candidate = build(` (copy ${n})`);
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return build(` (copy ${Date.now() % 1000})`);
}

/**
 * Clone a preset (same filters). Returns { ok, preset?, error?, list }.
 */
export function duplicateReportPreset(page, id, userId) {
  const list = getReportPresets(page, userId);
  if (!id) return { ok: false, error: 'Preset not found.', list };
  const source = list.find((item) => item.id === id);
  if (!source) return { ok: false, error: 'Preset not found.', list };
  if (list.length >= MAX) {
    return {
      ok: false,
      error: `You can save at most ${MAX} presets per page. Delete one before duplicating.`,
      list,
    };
  }

  const snap = filtersOnlySnapshot(source.snapshot);
  let name;
  try {
    name = assertValidSavedName(
      uniqueCopyName(source.name, list.map((item) => item.name)),
      { maxLength: PRESET_NAME_MAX, label: 'Preset name' },
    );
  } catch (err) {
    return { ok: false, error: err?.message || 'Invalid preset name.', list };
  }

  const preset = {
    id: makeId(),
    name,
    snapshot: snap,
    summary: summaryForPreset(snap),
    when: Date.now(),
    pinned: false,
    pinnedAt: null,
  };

  try {
    const next = writeList(page, userId, [preset, ...list]);
    const created = next.find((item) => item.id === preset.id) || preset;
    return { ok: true, preset: created, list: next };
  } catch {
    return { ok: false, error: 'Could not duplicate preset.', list: getReportPresets(page, userId) };
  }
}

export function toggleReportPresetPin(page, id, userId) {
  if (!id) return getReportPresets(page, userId);
  try {
    const existing = getReportPresets(page, userId);
    const next = existing.map((item) => {
      if (item.id !== id) return item;
      const pinned = !item.pinned;
      return {
        ...item,
        pinned,
        pinnedAt: pinned ? Date.now() : null,
        when: Date.now(),
      };
    });
    return writeList(page, userId, next);
  } catch {
    return getReportPresets(page, userId);
  }
}

export function removeReportPreset(page, id, userId) {
  try {
    const next = getReportPresets(page, userId).filter((item) => item.id !== id);
    return writeList(page, userId, next);
  } catch {
    return getReportPresets(page, userId);
  }
}

/** Build path+query to open this preset (include dates in snapshot when opening a page). */
export function hrefForPreset(page, snapshot) {
  const path = page === PRESET_PAGES.reporting
    ? '/reporting'
    : page === PRESET_PAGES.roi
      ? '/roi'
      : page === PRESET_PAGES.admob
        ? '/admob/dashboard'
        : page === PRESET_PAGES.admobReporting
          ? '/admob/reporting'
          : page === PRESET_PAGES.admobRoi
            ? '/admob/roi'
            : page === PRESET_PAGES.adsense
              ? '/adsense/dashboard'
              : page === PRESET_PAGES.adsenseReporting
                ? '/adsense/reporting'
                : page === PRESET_PAGES.adsenseRoi
                  ? '/adsense/roi'
                  : '/dashboard';
  const qs = encodeReportShare(normalizePresetSnapshot(snapshot));
  return qs ? `${path}?${qs}` : path;
}

export default {
  syncPresetsFromServer,
  getReportPresets,
  saveReportPreset,
  updateReportPreset,
  duplicateReportPreset,
  toggleReportPresetPin,
  removeReportPreset,
  summaryForPreset,
  normalizePresetSnapshot,
  filtersOnlySnapshot,
  mergePresetWithDates,
  hrefForPreset,
  PRESET_PAGES,
  PRESET_NAME_MAX,
  PRESETS_CHANGED_EVENT,
};
